import React, { Fragment } from 'react';
import { useForcedCartCache } from '../context/CartCacheContext';
import {
  calculateReservationDraftPricing,
  calculateServiceFeeLineItem,
} from '../../../../../../../shared/lib/reserve';
import { formatCentsToUSD } from '../../../../../../../shared/lib/utils';
import Button from './Button';
import { useApp } from '../context/AppContext';
import { useEventCache } from '../context/EventCacheContext';
import { evaluateAudienceZone } from '../../../../../../../shared/lib/audience';

interface CartSummaryProps {
  nextLabel?: string;
  nextDisabled?: boolean;
  onNext?: () => void;

  backLabel?: string;
  onBack?: () => void;

  loading?: boolean;
}

export default function CartSummary(props: CartSummaryProps) {
  const { config } = useApp();
  const { propertyServiceFeeInfo } = config;

  const { cart } = useForcedCartCache();
  const { allEvents } = useEventCache();

  const {
    onBack,
    onNext,
    nextDisabled,
    loading,
    nextLabel: nextTitle,
    backLabel: backTitle,
  } = props;

  const drafts = cart.workingDrafts.map(rd => rd.reservationDraft);
  const summaryPricing = calculateReservationDraftPricing(drafts);

  const serviceFeeLineItem = calculateServiceFeeLineItem(
    summaryPricing.summaryLineItems,
    propertyServiceFeeInfo
  );
  const finalTotal = summaryPricing.finalTotal + (serviceFeeLineItem?.price ?? 0);

  const dataCollections = allEvents?.collections ?? {
    segments: [],
    levels: [],
    attributes: [],
    zones: [],
  };

  return (
    <div className="grid grid-cols-1 gap-4">
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="grid grid-cols-1 gap-6 p-6 font-bold">
          <div className="text-xl text-accent">Pricing Summary</div>

          {cart.workingDrafts.map(rd => {
            const pricing = calculateReservationDraftPricing([rd.reservationDraft]);
            const eventTemplate = allEvents?.eventTemplates?.find(
              et => et._id === rd.reservationDraft.eventSnapshot?.eventTemplate
            );

            const visibleZones = dataCollections.zones
              .filter(z =>
                evaluateAudienceZone('visible', z._id, undefined, eventTemplate?.audience)
              )
              .map(z => (
                <div
                  key={z._id}
                  className="text-white shrink-0 min-w-0 bg-black text-sm font-bold rounded-full px-2 py-0 truncate go-event-zone"
                  style={{ backgroundColor: z.color }}
                >
                  {z.name}
                </div>
              ));

            return (
              <Fragment key={rd.reservationDraft._id}>
                <hr className="border-t border-border -mx-6" />
                <div>
                  {(visibleZones ?? []).length > 0 ? (
                    <div className="flex flex-wrap gap-1 mb-1 go-event-zones">{visibleZones}</div>
                  ) : null}
                  <div className="flex items-start justify-between">
                    <div className="mr-3">{rd.reservationDraft.eventSnapshot?.title}</div>
                    <div>{formatCentsToUSD(pricing.finalTotal)}</div>
                  </div>
                </div>
              </Fragment>
            );
          })}

          <hr className="border-t border-border -mx-6" />

          {serviceFeeLineItem && (
            <div className="flex items-start justify-between text-lg text-accent">
              <div className="mr-3">Service Fee</div>
              <div>{formatCentsToUSD(serviceFeeLineItem.price)}</div>
            </div>
          )}
          <div className="flex items-start justify-between text-lg text-accent">
            <div className="mr-3">Subtotal</div>
            <div>{formatCentsToUSD(finalTotal)}</div>
          </div>
          <div className="text-sm text-muted">Taxes calculated at checkout</div>
        </div>
      </div>
      {onBack || onNext ? (
        <div className="flex flex-wrap items-center gap-2">
          {onBack && (
            <Button
              variant="borderlike"
              onClick={onBack}
              className="shrink-0"
              icon={['fass', 'arrow-left']}
              loading={loading}
            >
              {backTitle ?? 'Back'}
            </Button>
          )}
          {onNext && (
            <Button
              variant="active"
              onClick={onNext}
              className="flex-grow"
              iconSuffix={['fass', 'arrow-right']}
              disabled={!!nextDisabled}
              loading={loading}
            >
              {nextTitle ?? 'Next'}
            </Button>
          )}
        </div>
      ) : null}
      <div>
        We use <span className="font-bold">Spark Cooperative</span> to manage reservations and{' '}
        <span className="font-bold">Stripe</span> to securely process payments. Your payment
        information is collected and handled by Stripe and is not stored on our servers.
      </div>
      <div>
        Charges will appear on your billing statement as{' '}
        <span className="font-bold">Spark GO Hospitality</span>.
      </div>
    </div>
  );
}
